import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  Card, 
  Col, 
  message,
  DatePicker,
  Drawer,
  Spin,
  Tag
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import { useNavigate } from 'react-router-dom';
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FilterPanel from '../../../../components/common/FilterPanel';
import OrderDetailContent from '../components/OrderDetailContent';
import * as orderService from '../../../../services/order/orderService';
import { ORDER_STATUS_OPTIONS, CHANNEL_OPTIONS, PAYMENT_METHOD_OPTIONS, ORDER_ITEM_STATUS_OPTIONS, SHORTAGE_REASON_OPTIONS, PICKING_MODE_OPTIONS, CANCEL_TYPE_OPTIONS } from '../../../../constants/order';
import { ORDER_STATUS, CHANNEL, PAYMENT_METHOD, PICKING_MODE } from '../../../../constants/order';

const { RangePicker } = DatePicker;

/**
 *订单列表页
 *路: /order/order/list
 */
const OrderList = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
  });

  // Drawer状态管理
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  // 打开详情Drawer
  const handleViewDetail = async (orderNo) => {
    setDrawerVisible(true);
    setDrawerLoading(true);
    try {
      const response = await orderService.getOrderByOrderNo(orderNo);
      if (response.success) {
        setCurrentOrder(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询订单详情失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setDrawerLoading(false);
    }
  };

  // 关闭Drawer
  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setCurrentOrder(null);
  };

  //表格列定义
  const columns = [
    {
      title: t('order.order.list.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      fixed: 'left',
      width: 280,
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewDetail(record.orderNo)}
          style={{ padding: 0, whiteSpace: 'nowrap' }}
        >
          {text}
        </Button>
      )
    },
    {
      title: t('order.order.list.table.orderType'),
      dataIndex: 'orderType',
      key: 'orderType',
      width: 100,
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: t('order.order.list.table.pickingMode'),
      dataIndex: 'pickingMode',
      key: 'pickingMode',
      width: 130,
      ellipsis: true,
      render: (pickingMode) => {
        const option = PICKING_MODE_OPTIONS.find(opt => opt.value === pickingMode);
        return option ? t(option.label) : (pickingMode || '-');
      }
    },
    {
      title: t('order.order.list.table.orderStatus'),
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 180,
      ellipsis: true,
      render: (status) => {
        const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
        const colorMap = {
          'blue': '#1890ff',
          'processing': '#1890ff',
          'green': '#52c41a',
          'default': '#8c8c8c'
        };
        const color = colorMap[option?.color] || '#8c8c8c';
        const displayText = t(option?.label || status);
        return (
          <span 
            style={{ 
              color: color,
              fontWeight: '500'
            }}
            title={displayText}
          >
            {displayText}
          </span>
        );
      }
    },
    {
      title: t('order.order.list.table.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      ellipsis: true,
      render: (channel) => {
        const option = CHANNEL_OPTIONS.find(opt => opt.value === channel);
        return option?.label || channel;
      }
    },
    {
      title: t('order.order.list.table.paymentMethod'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 180,
      ellipsis: true,
      render: (paymentMethod) => {
        const option = PAYMENT_METHOD_OPTIONS.find(opt => opt.value === paymentMethod);
        return option?.label || paymentMethod;
      }
    },
    {
      title: t('order.order.list.table.skuQty'),
      dataIndex: 'skuQty',
      key: 'skuQty',
      width: 80,
      align: 'center'
    },
    {
      title: t('order.order.list.table.totalMu'),
      dataIndex: 'totalMu',
      key: 'totalMu',
      width: 80,
      align: 'center'
    },
    {
      title: t('order.order.list.table.totalAmount'),
      dataIndex: 'subTotal',
      key: 'subTotal',
      width: 120,
      align: 'right',
      render: (amount) => `฿${amount?.toFixed(3) || '0.000'}`
    },
    {
      title: t('order.order.list.table.grandTotal'),
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      width: 140,
      align: 'right',
      render: (amount) => amount != null ? `฿${amount.toFixed(3)}` : '-'
    },
    {
      title: t('order.order.list.table.remark'),
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: t('order.order.list.table.isReturn'),
      dataIndex: 'isReturn',
      key: 'isReturn',
      width: 100,
      align: 'center',
      render: (v) => (
        <Tag color={v ? 'red' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>
      )
    },
    {
      title: t('order.order.list.table.hasOos'),
      dataIndex: 'hasOos',
      key: 'hasOos',
      width: 100,
      align: 'center',
      render: (v) => (
        <Tag color={v ? 'orange' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>
      )
    },
    {
      title: t('order.order.list.table.hasSubstitution'),
      dataIndex: 'hasSubstitution',
      key: 'hasSubstitution',
      width: 150,
      align: 'center',
      render: (v) => (
        <Tag color={v ? 'blue' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>
      )
    },
    {
      title: t('order.order.list.table.deadlineTime'),
      dataIndex: 'deadlineTime',
      key: 'deadlineTime',
      width: 180,
      render: (time) => time ? dayjs(time).format('DD-MM-YYYY HH:mm:ss') : '-'
    },
    {
      title: t('order.order.list.table.timeout'),
      dataIndex: 'timeout',
      key: 'timeout',
      width: 100,
      align: 'center',
      render: (v, record) => {
        const canCalcTimeout = record.orderStatus === ORDER_STATUS.CREATED || record.orderStatus === ORDER_STATUS.IN_PROGRESS;
        const isTimeout = v || (canCalcTimeout && record.deadlineTime && dayjs(record.deadlineTime).isBefore(dayjs()));
        return (
          <Tag color={isTimeout ? 'red' : 'default'}>{isTimeout ? t('common.yes') : t('common.no')}</Tag>
        );
      }
    },
    {
      title: t('order.order.list.table.cancelTime'),
      dataIndex: 'cancelTime',
      key: 'cancelTime',
      width: 180,
      render: (time) => time ? dayjs(time).format('DD-MM-YYYY HH:mm:ss') : '-'
    },
    {
      title: t('order.order.list.table.cancelLabel'),
      dataIndex: 'cancelLabel',
      key: 'cancelLabel',
      width: 110,
      align: 'center',
      render: (val) => (
        <Tag color={val ? 'red' : 'default'}>{val ? t('common.yes') : t('common.no')}</Tag>
      )
    },
    {
      title: t('order.order.list.table.cancelType'),
      dataIndex: 'cancelType',
      key: 'cancelType',
      width: 140,
      ellipsis: true,
      render: (text) => {
        if (!text) return '-';
        const option = CANCEL_TYPE_OPTIONS.find((opt) => opt.value === text);
        return t(option?.label || text);
      }
    },
    {
      title: t('order.order.list.table.cancelConfirmBy'),
      dataIndex: 'cancelConfirmBy',
      key: 'cancelConfirmBy',
      width: 140,
      ellipsis: true,
      render: (text) => text || '-'
    },

    {
      title: t('order.order.list.table.buyerName'),
      dataIndex: 'buyerName',
      key: 'buyerName',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('order.order.list.table.buyerPhone'),
      dataIndex: 'buyerPhone',
      key: 'buyerPhone',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('order.order.list.table.createTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (createTime) => dayjs(createTime).format('DD-MM-YYYY HH:mm:ss')
    },
    {
      title: t('order.order.list.table.pickingCompletedTime'),
      dataIndex: 'pickingEndTime',
      key: 'pickingEndTime',
      width: 180,
      render: (time) => time ? dayjs(time).format('DD-MM-YYYY HH:mm:ss') : '-'
    },
    {
      title: t('order.order.list.table.actions'),
      key: 'actions',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small"
            onClick={() => handleViewDetail(record.orderNo)}
          >
            {t('order.order.list.actions.view')}
          </Button>
        </Space>
      )
    }
  ];

  // 获取数据
  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        ...form.getFieldsValue(),
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...params
      };

      //处时间范围
      if (searchParams.createTime && searchParams.createTime.length === 2) {
        searchParams.createTimeStart = searchParams.createTime[0].valueOf();
        searchParams.createTimeEnd = searchParams.createTime[1].valueOf();
        delete searchParams.createTime;
      }

      const response = await orderService.pageOrder(searchParams);

      if (response.success) {
        const data = response.data || {};
        setDataSource(data.tableData || []);
        setPagination(prev => ({
          ...prev,
          current: data.paging?.currentPage || 1,
          total: data.paging?.totalCount || 0
        }));
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询订单失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  //初加载数据
  useEffect(() => {
    fetchData();
  }, []);

  //处理搜索
  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  // 导出
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const searchParams = { ...form.getFieldsValue() };
      // 处理时间范围
      if (searchParams.createTime && searchParams.createTime.length === 2) {
        searchParams.createTimeStart = searchParams.createTime[0].valueOf();
        searchParams.createTimeEnd = searchParams.createTime[1].valueOf();
        delete searchParams.createTime;
      }
      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await orderService.exportOrder(searchParams);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('common.message.exportSuccess') || 'Export successful');
      } else {
        message.destroy();
        message.error(response.errorMessage || t('common.message.exportFailed') || 'Export failed');
      }
    } catch (error) {
      message.destroy();
      console.error('导出订单失败:', error);
      message.error(t('common.message.exportFailed') || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // 分页变化
  const handleTableChange = (paginationConfig) => {
    const newPagination = {
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    };
    setPagination(newPagination);
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/*搜索区域 */}
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        title={t('order.order.list.title')}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item 
            name="orderNo" 
            label={t('order.order.list.search.orderNo')}
          >
            <Input placeholder={t('order.order.list.search.placeholder.orderNo')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="orderType" 
            label={t('order.order.list.search.orderType')}
          >
            <Input placeholder={t('order.order.list.search.placeholder.orderType')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="channel" 
            label={t('order.order.list.search.channel')}
          >
            <Select 
              placeholder={t('order.order.list.search.placeholder.channel')}
              options={CHANNEL_OPTIONS.map(option => ({
                value: option.value,
                label: option.label
              }))}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="paymentMethod" 
            label={t('order.order.list.search.paymentMethod')}
          >
            <Select 
              placeholder={t('order.order.list.search.placeholder.paymentMethod')}
              options={PAYMENT_METHOD_OPTIONS.map(option => ({
                value: option.value,
                label: option.label
              }))}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="orderStatus" 
            label={t('order.order.list.search.orderStatus')}
          >
            <Select 
              placeholder={t('order.order.list.search.placeholder.orderStatus')}
              options={ORDER_STATUS_OPTIONS.map(option => ({
                value: option.value,
                label: t(option.label)
              }))}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="isReturn" 
            label={t('order.order.list.search.isReturn')}
          >
            <Select 
              placeholder={t('order.order.list.search.placeholder.isReturn')}
              options={[
                { value: true, label: t('common.yes') },
                { value: false, label: t('common.no') }
              ]}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="buyerName" 
            label={t('order.order.list.search.buyerName')}
          >
            <Input placeholder={t('order.order.list.search.placeholder.buyerName')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="cancelConfirmBy" 
            label={t('order.order.list.search.cancelConfirmBy')}
          >
            <Input placeholder={t('order.order.list.search.placeholder.cancelConfirmBy')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item 
            name="createTime" 
            label={t('order.order.list.search.createTime')}
          >
            <RangePicker 
              showTime 
              format="DD-MM-YYYY HH:mm:ss"
              placeholder={[
                t('common.pleaseInput'),
                t('common.pleaseInput')
              ]}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 操作栏 */}
      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
          onClick={handleExport}
          loading={exportLoading}
        >
          {t('common.export')}
        </Button>
      </div>

      {/* 数据表格 */}
      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="orderNo"
            scroll={{ x: 3300, y: scrollY }}
          />
        </div>
      </Card>

      {/* 订单详情Drawer - 符合UI/UX规范 */}
      <Drawer
        title={t('order.order.detail.title')}
        placement="right"
        width={960}
        onClose={handleCloseDrawer}
        open={drawerVisible}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={handleCloseDrawer}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        <Spin spinning={drawerLoading}>
          {currentOrder && <OrderDetailContent orderDetail={currentOrder} />}
        </Spin>
      </Drawer>
    </div>
  );
};

export default OrderList;
